import { useContext, useEffect, useState } from 'react'
import PageWrapper from '../../layout/PageWrapper/PageWrapper'
import Page from '../../layout/Page/Page'
import Card, { CardActions, CardBody, CardHeader, CardLabel, CardTitle } from '../bootstrap/Card'
import Input from '../bootstrap/forms/Input'
import Button, { ButtonGroup } from '../bootstrap/Button'
import { useFormik } from 'formik'
import Icon from '../icon/Icon'
import { TableLoader } from '../../services/loader.services'
import NoDataMsg from '../../common/components/NoDataMsg'
import useSortableData from '../../hooks/useSortableData'
import PaginationButtons, { dataPagination } from '../PaginationButtons'
import { toasts } from '../../services/toaste.service'
import { assignProduct, deleteProductAssign, getProductAssignDetails, getProductDetailsStock } from '../../services/productService'
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../bootstrap/Modal'
import FormGroup from '../bootstrap/forms/FormGroup'
import SearchableSelect from '../../common/components/SearchableSelect'
import { getCompanyDetails } from '../../services/common.service'
import { getLicenseKey } from '../../services/application.settings'
import AuthContext from '../../contexts/authContext'
import { showLoader } from '../../services/loader.services'
import AlertService from '../../services/alert.service'

const AssignProducts = () => {

    useEffect(() => {
        loadDispatchProductList()
        loadCompanyList()
        loadProductDetails()
    }, [])

    const { userAccountId } = useContext(AuthContext)

    // ─── Search form ─────────────────────────────────
    const columnVisibileForm = useFormik({
        enableReinitialize: true,
        initialValues: { searchInput: '', available: false },
        validate: () => { },
        onSubmit: () => { },
    })

    // ─── Assign modal form ────────────────────────────
    const assignForm = useFormik({
        enableReinitialize: true,
        initialValues: {
            companyDetailsId: '',
            deliveryLocation: '',
            truckNo: '',
        },
        validate: (values: any) => {
            const errors: { companyDetailsId?: string; truckNo?: string } = {}
            if (!values.truckNo) errors.truckNo = 'Required'
            if (!companyDetailsId) errors.companyDetailsId = 'Required'
            return errors
        },
        onSubmit: () => { submitAssignApi() },
    })

    // ─── List states ─────────────────────────────────
    const [dispatchProductList, setDispatchProductList] = useState<any>([])
    const [noDataMsg, setNoDataMsg] = useState('')
    const [dataStatus, setDataStatus] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [perPage, setPerPage] = useState(10)
    const { items, requestSort, getClassNamesFor } = useSortableData(dispatchProductList)
    const onCurrentPageData = dataPagination(items, currentPage, perPage)


    // ─── Modal states ─────────────────────────────────
    const [isOffCanvasOpen, setIsOffCanvasOpen] = useState(false)
    const [companyList, setCompanyList] = useState<any>([])
    const [companyDetailsId, setCompanyDetailsId] = useState<any>('')

    // Edit mode state
    const [editingDispatchProductId, setEditingDispatchProductId] = useState<any>(0)

    // For Alert (same pattern as CompanyDetails.tsx)
    const [alertStatus, setAlertStatus] = useState<any>({ message: "", type: "" })
    const [isAlertOpen, setIsAlertOpen] = useState<any>(false)

    const [lotOptions, setLotOptions] = useState<any>([])                // unique lots

    // Dispatch detail rows
    const [dispatchRows, setDispatchRows] = useState<any>([
        { lotId: '', countId: '', noOfProduct: '', maxProduct: 0, productDetailsCountId: '', countOptions: [], noOfProductError: '', lotError: '', countError: '', duplicateError: '', fromBagNo: '', toBagNo: '', fromBagNoError: '', toBagNoError: '' }
    ])

    // Delete Modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [selectedDetailId, setSelectedDetailId] = useState<any>(null)

    // ─── Filtered search ─────────────────────────────
    const filteredData: any = onCurrentPageData.filter(
        (i: any) =>
            (i.companyName !== null && i.companyName.toString().toLowerCase().includes(columnVisibileForm.values.searchInput.toLowerCase())) ||
            (i.deliveryLocation !== null && i.deliveryLocation.toString().toLowerCase().includes(columnVisibileForm.values.searchInput.toLowerCase())) ||
            (i.vehicleNo !== null && i.vehicleNo.toString().toLowerCase().includes(columnVisibileForm.values.searchInput.toLowerCase()))
    )

    // ─── Load list ────────────────────────────────────
    function loadDispatchProductList() {
        setDataStatus(false)
        getProductAssignDetails(
            (response) => {
                if (response.data.success) {
                    const data = response.data.data.productAssignDetails
                    if (data != undefined) {
                        data.forEach((d: any, i: number) => { d.sno = i + 1; d.isOpen = false })
                        setDispatchProductList(data)
                        setDataStatus(true)
                    } else {
                        toasts('Undefined Data', 'Error')
                    }
                } else if (response.data.success === false) {
                    setNoDataMsg(response.data.message)
                    setDataStatus(true)
                } else {
                    toasts(response.data.error[0].error_description, 'Error')
                }
            },
            (error) => { toasts(error, 'Error') }
        )
    }

    // ─── Load companies ───────────────────────────────
    function loadCompanyList() {
        getCompanyDetails(0,
            (response) => {
                if (response.data.success) {
                    const data = response.data.data.companyDetails
                    if (data != undefined) setCompanyList(data)
                } else if (response.data.success === false) {
                    setCompanyList([])
                } else {
                    toasts(response.data.error[0].error_description, 'Error')
                }
            },
            (error) => { toasts(error, 'Error') }
        )
    }

    // ─── Load product details count (lot + count) ───────────
    function loadProductDetails() {
        // lotId = 0 => lot dropdown list
        getProductDetailsStock(0, 1,
            (response) => {
                if (response.data.success) {
                    const data = response.data.data.productDetailsStock
                    if (data != undefined) {
                        // Build unique lot options
                        const uniqueLots: any = []
                        const seen = new Set()
                        data.forEach((item: any) => {
                            if (!seen.has(item.lotId)) {
                                seen.add(item.lotId)
                                uniqueLots.push({ value: item.lotId, label: item.lotNo })
                            }
                        })
                        setLotOptions(uniqueLots)
                    }
                }
            },
            (error) => { toasts(error, 'Error') }
        )
    }

    // ─── Row expand/collapse ──────────────────────────
    function onView(companyDetailsId: any, vehicleNo: any, isOpen: any) {
        setDispatchProductList((prev: any) =>
            prev.map((item: any) => ({
                ...item,
                isOpen: (item.companyDetailsId === companyDetailsId && item.vehicleNo === vehicleNo) ? !isOpen : false
            }))
        )
    }

    // ─── Modal helpers ────────────────────────────────
    function openAssignCanvas() {
        setCompanyDetailsId('')
        assignForm.resetForm()
        setDispatchRows([{ lotId: '', countId: '', noOfProduct: '', maxProduct: 0, productDetailsCountId: '', countOptions: [], noOfProductError: '', lotError: '', countError: '', duplicateError: '', fromBagNo: '', toBagNo: '', fromBagNoError: '', toBagNoError: '' }])
        setEditingDispatchProductId(0)
        setIsOffCanvasOpen(true)
    }

    function openEditModal(item: any) {
        // Stop row expand click
        setEditingDispatchProductId(item?.dispatchProductId || item?.companyDetailsId + item?.vehicleNo)
        setCompanyDetailsId({ value: item?.companyDetailsId, label: item?.companyName })
        assignForm.setValues({
            companyDetailsId: item?.companyDetailsId,
            deliveryLocation: item?.deliveryLocation || '',
            truckNo: item?.vehicleNo || ''
        })

        const initialRows = (item?.productDetails ?? []).map((d: any) => ({
            dispatchProductDetailsId: d?.productAssignId || d?.dispatchProductDetailsId,
            totalScannedCount: d?.pendingCount ?? d?.totalScannedCount ?? 0,
            stockCountFromDispatch: d?.stockCount ?? d?.assignedCount ?? 0,
            lotId: { value: d?.lotId, label: d?.lotNo },
            countId: { value: d?.productDetailsStockId ?? d?.productDetailsCountId ?? d?.countId, label: d?.labelName || d?.countNo, productDetailsCountId: d?.productDetailsStockId ?? d?.productDetailsCountId },
            noOfProduct: String(d?.assignedCount ?? d?.noOfProduct ?? ''),
            maxProduct: Math.max(0, Number((d?.maximumAllowedAssignedCount !== undefined && d?.maximumAllowedAssignedCount !== -1) ? d?.maximumAllowedAssignedCount : (d?.stockCount ?? d?.assignedCount ?? 0))),
            productDetailsCountId: d?.productDetailsStockId ?? d?.productDetailsCountId,
            fromBagNo: d?.fromBagNo ?? '',
            toBagNo: d?.toBagNo ?? '',
            allData: d,
            countOptions: [],
            noOfProductError: '',
            lotError: '',
            countError: '',
            duplicateError: '',
            fromBagNoError: '',
            toBagNoError: ''
        }))

        setDispatchRows(initialRows.length ? initialRows : [{ lotId: '', countId: '', noOfProduct: '', maxProduct: 0, productDetailsCountId: '', countOptions: [], noOfProductError: '', lotError: '', countError: '', duplicateError: '', fromBagNo: '', toBagNo: '', fromBagNoError: '', toBagNoError: '' }])
        setIsOffCanvasOpen(true)

        // Load count options for each row's lot.
        // Note: In edit mode, maxProduct must remain based on getDispatchProduct's stockCount.
        initialRows.forEach((row: any, idx: number) => {
            const lotId = row?.lotId?.value
            if (!lotId) return
            getProductDetailsStock(lotId, 1,
                (response) => {
                    if (!response.data.success) return
                    const data = response.data.data.productDetailsStock
                    if (!data) return
                    setDispatchRows((prev: any) => {
                        const updated = [...prev]
                        if (!updated[idx]) return prev
                        updated[idx].countOptions = data.map((c: any) => ({
                            value: c.productDetailsStockId,
                            label: c.labelName,
                            productDetailsCountId: c.productDetailsStockId ?? c.productDetailsCountId,
                            maxCount: c.availableStockCount ?? c.stockCount ?? 0,
                            allData: c,
                        }))
                        const selected = updated[idx].countId
                        const found = updated[idx].countOptions.find((o: any) => o.value == selected?.value || o.allData?.countId == selected?.value)
                        if (found) {
                            // enrich selected object with allData for stockCount check and update label with the full labelName
                            updated[idx].countId = { ...selected, label: found.label, productDetailsCountId: found.productDetailsCountId, allData: found.allData }
                            // keep updated[idx].maxProduct as-is (from dispatch stockCount)
                        }
                        return updated
                    })
                },
                () => { }
            )
        })
    }

    function onLotChange(index: number, selectedLot: any) {
        const rows = [...dispatchRows]
        rows[index].lotId = selectedLot
        rows[index].countId = ''
        rows[index].noOfProduct = ''
        rows[index].maxProduct = 0
        rows[index].productDetailsCountId = ''
        rows[index].noOfProductError = ''
        rows[index].lotError = ''
        rows[index].countError = ''
        rows[index].duplicateError = ''
        rows[index].countOptions = []
        setDispatchRows(rows)

        // lotId pass panni count dropdown list eduthuka
        getProductDetailsStock(selectedLot?.value, 1,
            (response) => {
                if (response.data.success) {
                    const data = response.data.data.productDetailsStock
                    if (data != undefined) {
                        const updated = [...rows]
                        updated[index].countOptions = data.map((d: any) => ({
                            value: d.productDetailsStockId,
                            label: d.labelName,
                            productDetailsCountId: d.productDetailsStockId ?? d.productDetailsCountId,
                            maxCount: d.availableStockCount ?? d.stockCount ?? 0,
                            allData: d,
                        }))
                        setDispatchRows(updated)
                    }
                } else if (response.data.success === false) {
                    const updated = [...rows]
                    updated[index].countOptions = []
                    setDispatchRows(updated)
                }
            },
            (error) => { toasts(error, 'Error') }
        )
    }

    function onCountChange(index: number, selectedCount: any) {
        const rows = [...dispatchRows]
        rows[index].countId = selectedCount
        rows[index].noOfProduct = ''
        rows[index].noOfProductError = ''
        rows[index].countError = ''
        rows[index].duplicateError = ''
        const lotId = rows[index].lotId?.value
        const countId = selectedCount?.value

        // Edit mode: max allowed should be based on stockCount
        // Add mode: max allowed should be based on availableStockCount
        // Check if this is the original assignment being edited (same Lot & Count/ProductDetailsStock)
        const isOriginalAssignment = rows[index]?.dispatchProductDetailsId &&
            (selectedCount?.value === rows[index]?.allData?.productDetailsStockId || selectedCount?.allData?.countId === rows[index]?.allData?.countId) &&
            rows[index]?.lotId?.value === rows[index]?.allData?.lotId;

        if (isOriginalAssignment) {
            // For existing assignments, use maximumAllowedAssignedCount from getProductAssignDetails
            const maxFromDispatch = Number((rows[index]?.allData?.maximumAllowedAssignedCount !== undefined && rows[index]?.allData?.maximumAllowedAssignedCount !== -1) ? rows[index]?.allData?.maximumAllowedAssignedCount : (rows[index]?.allData?.stockCount ?? rows[index]?.stockCountFromDispatch ?? 0))
            rows[index].maxProduct = Math.max(0, maxFromDispatch)
        } else {
            // For new rows or if product is changed, use maximumAllowedAssignedCount from getProductDetailsStock
            const maxFromApi = Number((selectedCount?.allData?.maximumAllowedAssignedCount !== undefined && selectedCount?.allData?.maximumAllowedAssignedCount !== -1) ? selectedCount?.allData?.maximumAllowedAssignedCount : (selectedCount?.allData?.availableStockCount ?? selectedCount?.allData?.stockCount ?? 0))
            rows[index].maxProduct = Math.max(0, maxFromApi)
        }
        rows[index].productDetailsCountId = selectedCount?.productDetailsCountId ?? selectedCount?.value
        // Duplicate check: same lotId + countId in another row
        const isDuplicate = rows.some((r: any, i: number) =>
            i !== index &&
            r.lotId?.value == rows[index].lotId?.value &&
            r.countId?.value == selectedCount?.value
        )
        if (isDuplicate) rows[index].duplicateError = 'Duplicate entry'
        setDispatchRows(rows)
    }

    function onNoOfProductChange(index: number, value: string) {
        const rows = [...dispatchRows]
        const num = Number(value)
        rows[index].noOfProduct = value
        if (value === '') {
            rows[index].noOfProductError = ''
        } else if (isNaN(num) || num <= 0) {
            rows[index].noOfProductError = 'Enter a valid number'
        } else if (num > rows[index].maxProduct) {
            rows[index].noOfProductError = `Max allowed is ${rows[index].maxProduct}`
        } else {
            rows[index].noOfProductError = ''
        }
        setDispatchRows(rows)
    }

    function onFromBagNoChange(index: number, value: string) {
        const rows = [...dispatchRows]
        rows[index].fromBagNo = value
        rows[index].fromBagNoError = ''
        setDispatchRows(rows)
    }

    function onToBagNoChange(index: number, value: string) {
        const rows = [...dispatchRows]
        rows[index].toBagNo = value
        rows[index].toBagNoError = ''
        setDispatchRows(rows)
    }

    function addRow() {
        setDispatchRows([...dispatchRows, { lotId: '', countId: '', noOfProduct: '', maxProduct: 0, productDetailsCountId: '', countOptions: [], noOfProductError: '', lotError: '', countError: '', duplicateError: '', fromBagNo: '', toBagNo: '', fromBagNoError: '', toBagNoError: '' }])
    }

    function removeRow(index: number) {
        const rows = dispatchRows.filter((_: any, i: number) => i !== index)
        setDispatchRows(rows)
    }

    function submitAssign() {
        assignForm.submitForm()
    }

    function submitAssignApi() {
        if (!assignForm.isValid) { setAlertStatus({ message: 'Please fill all details', type: 'error' }); setIsAlertOpen(true); return }
        if (!companyDetailsId?.value) { setAlertStatus({ message: 'Please select Company', type: 'error' }); setIsAlertOpen(true); return }

        // Mark row-level inline errors
        let hasRowError = false
        const updatedRows = dispatchRows.map((row: any, idx: number) => {
            const r = { ...row }
            r.lotError = !r.lotId?.value ? 'Required' : ''
            r.countError = !r.countId?.value ? 'Required' : ''
            if (!r.noOfProduct && !r.noOfProductError) r.noOfProductError = 'Required'
            // Duplicate check
            const isDuplicate = dispatchRows.some((other: any, otherIdx: number) =>
                otherIdx !== idx &&
                other.lotId?.value == r.lotId?.value &&
                other.countId?.value == r.countId?.value &&
                r.lotId?.value && r.countId?.value
            )
            if (isDuplicate) r.duplicateError = 'Duplicate entry'
            if (!r.lotId?.value || !r.countId?.value || !r.noOfProduct || r.noOfProductError || r.duplicateError) hasRowError = true
            return r
        })
        setDispatchRows(updatedRows)
        if (hasRowError) return
        showLoader(true)
        const postData = {
            licenseKey: getLicenseKey,
            companyDetailsId: companyDetailsId?.value,
            deliveryLocation: assignForm.values.deliveryLocation ? assignForm.values.deliveryLocation.trim() : '',
            vehicleNo: assignForm.values.truckNo.trim(),
            userAccountId: userAccountId,
            productDetails: dispatchRows.map((row: any) => ({
                productAssignId: row.dispatchProductDetailsId > 0 ? row.dispatchProductDetailsId : 0,
                productDetailsStockId: row.productDetailsCountId || row.countId?.productDetailsCountId || row.countId?.value,
                assignedCount: Number(row.noOfProduct),
                fromBagNo: row.fromBagNo ? row.fromBagNo.trim() : null,
                toBagNo: row.toBagNo ? row.toBagNo.trim() : null
            }))
        }

        assignProduct(postData,
            (response) => {
                showLoader(false)
                if (response.data.success) {
                    setAlertStatus({ message: response.data.message, type: 'success' })
                    setIsAlertOpen(true)
                    setIsOffCanvasOpen(false)
                    setEditingDispatchProductId(0)
                    loadDispatchProductList()
                } else if (response.data.success === false) {
                    setAlertStatus({ message: response.data.message, type: 'error' })
                    setIsAlertOpen(true)
                } else {
                    setAlertStatus({ message: response.data.error[0].error_description, type: 'error' })
                    setIsAlertOpen(true)
                }
            },
            (error) => { showLoader(false); setAlertStatus({ message: error, type: 'error' }); setIsAlertOpen(true) }
        )
    }

    function onDelete(dispatchProductDetailsId: any) {
        setSelectedDetailId(dispatchProductDetailsId)
        setIsDeleteModalOpen(true)
    }

    function confirmDelete() {
        if (!selectedDetailId) return
        showLoader(true)
        const postData = {
            licenseKey: getLicenseKey,
            productAssignId: selectedDetailId,
            userAccountId: userAccountId,
        }
        deleteProductAssign(postData,
            (response) => {
                showLoader(false)
                if (response.data.success) {
                    setAlertStatus({ message: response.data.message, type: 'success' })
                    setIsAlertOpen(true)
                    setIsDeleteModalOpen(false)
                    loadDispatchProductList()
                } else {
                    setAlertStatus({ message: response.data.message || 'Failed to delete', type: 'error' })
                    setIsAlertOpen(true)
                }
            },
            (error) => {
                showLoader(false)
                setAlertStatus({ message: error, type: 'error' })
                setIsAlertOpen(true)
            }
        )
    }

    return (
        <PageWrapper title={`Assigned Product List`}>
            <Page>
                <Card stretch data-tour='list'>
                    <CardHeader borderSize={1}>
                        <CardLabel icon='List' iconColor='info' className='col-lg-6'>
                            <CardTitle tag='div' className='h5'>Assigned Product List</CardTitle>
                        </CardLabel>

                        <CardActions>
                            <ButtonGroup className='d-print-none' color='primary'>
                                <Icon className='mt-1' icon='Search' size='2x' color='primary' />
                                <Input
                                    id='searchInput'
                                    type='search'
                                    placeholder='Search...'
                                    onChange={columnVisibileForm.handleChange}
                                    value={columnVisibileForm.values.searchInput}
                                />
                            </ButtonGroup>


                            <Button
                                color='primary'
                                icon='Add'
                                onClick={openAssignCanvas}
                            >
                                Assign
                            </Button>
                        </CardActions>
                    </CardHeader>

                    <CardBody>
                        {!dataStatus ?
                            <TableLoader /> :
                            <div className='table-responsive'>
                                <table className='table table-modern table-hover small'>
                                    <thead className='table-primary'>
                                        <tr>
                                            <th scope='col' onClick={() => requestSort('sno')} className='cursor-pointer py-3 border-bottom-0 bg-light rounded-start' style={{ width: '60px' }}>
                                                <span className='small text-uppercase fw-bold'>S.No</span>
                                                <Icon size='lg' className={getClassNamesFor('sno')} icon='FilterList' />
                                            </th>
                                            <th scope='col' onClick={() => requestSort('companyName')} className='cursor-pointer py-3 border-bottom-0 bg-light'>
                                                <span className='small text-uppercase fw-bold'>Company Name</span>
                                                <Icon size='lg' className={getClassNamesFor('companyName')} icon='FilterList' />
                                            </th>
                                            <th scope='col' onClick={() => requestSort('deliveryLocation')} className='cursor-pointer py-3 border-bottom-0 bg-light'>
                                                <span className='small text-uppercase fw-bold'>Delivery Location</span>
                                                <Icon size='lg' className={getClassNamesFor('deliveryLocation')} icon='FilterList' />
                                            </th>
                                            <th scope='col' onClick={() => requestSort('stateName')} className='cursor-pointer py-3 border-bottom-0 bg-light'>
                                                <span className='small text-uppercase fw-bold'>State</span>
                                                <Icon size='lg' className={getClassNamesFor('stateName')} icon='FilterList' />
                                            </th>
                                            <th scope='col' onClick={() => requestSort('cityName')} className='cursor-pointer py-3 border-bottom-0 bg-light'>
                                                <span className='small text-uppercase fw-bold'>City</span>
                                                <Icon size='lg' className={getClassNamesFor('cityName')} icon='FilterList' />
                                            </th>
                                            <th scope='col' onClick={() => requestSort('vehicleNo')} className='cursor-pointer py-3 border-bottom-0 bg-light'>
                                                <span className='small text-uppercase fw-bold'>Vehicle No</span>
                                                <Icon size='lg' className={getClassNamesFor('vehicleNo')} icon='FilterList' />
                                            </th>
                                            <th scope='col' className='text-center py-3 border-bottom-0 bg-light rounded-end' style={{ width: '130px' }}>
                                                <span className='small text-uppercase fw-bold'>Action</span>
                                            </th>
                                        </tr>
                                    </thead>

                                    {filteredData != '' ? <>
                                        {filteredData.map((item: any) => (
                                            <tbody key={`${item.companyDetailsId}-${item.vehicleNo}`} className='border-top-0'>
                                                <tr onClick={() => onView(item.companyDetailsId, item.vehicleNo, item.isOpen)} className={`${item.isOpen ? 'bg-l10-info' : ''} shadow-hover-sm transition-base cursor-pointer`}>
                                                    <td className='py-3'>{item.sno}</td>
                                                    <td className='py-3'>
                                                        <span style={{ fontSize: '12px' }}>{item?.companyName}</span>
                                                    </td>
                                                    <td className='py-3'>
                                                        <span style={{ fontSize: '12px' }}>{item?.deliveryLocation}</span>
                                                    </td>
                                                    <td className='py-3'>
                                                        <span style={{ fontSize: '12px' }}>{item?.stateName}</span>
                                                    </td>
                                                    <td className='py-3'>
                                                        <span style={{ fontSize: '12px' }}>{item?.cityName}</span>
                                                    </td>
                                                    <td className='py-3'>
                                                        <span style={{ fontSize: '12px' }}>{item?.vehicleNo}</span>
                                                    </td>
                                                    <td className='text-center py-3'>
                                                        <Button
                                                            color='primary'
                                                            isLight
                                                            icon='Edit'
                                                            className='rounded-pill shadow-sm me-2'
                                                            onClick={(e: any) => { e.stopPropagation(); openEditModal(item) }}
                                                            title='Edit Assignment'
                                                        />
                                                        <Icon
                                                            size={'2x'}
                                                            icon={item.isOpen ? 'KeyboardArrowUp' : 'KeyboardArrowDown'}
                                                            color={item.isOpen ? 'primary' : 'dark'}
                                                        />
                                                    </td>
                                                </tr>
                                                {item.isOpen ?
                                                    <tr className='bg-light'>
                                                        <td colSpan={7} className='p-4'>
                                                            <div className='card shadow-sm border-0 rounded-3'>
                                                                <div className='table-responsive'>
                                                                    <table className='table table-modern table-hover mb-0'>
                                                                        <thead className='table-info'>
                                                                            <tr>
                                                                                <th scope='col' className='py-3 border-0 rounded-start'><span className='text-uppercase fw-bold ps-2'>#</span></th>
                                                                                <th scope='col' className='py-3 border-0'><span className='text-uppercase fw-bold'>Lot No</span></th>
                                                                                <th scope='col' className='py-3 border-0'><span className='text-uppercase fw-bold'>Count No</span></th>
                                                                                <th scope='col' className='py-3 border-0'><span className='text-uppercase fw-bold'>No Of Cone</span></th>
                                                                                <th scope='col' className='py-3 border-0'><span className='text-uppercase fw-bold'>Cone Weight</span></th>
                                                                                <th scope='col' className='py-3 border-0 text-center'><span className='text-uppercase fw-bold'>From Bag</span></th>
                                                                                <th scope='col' className='py-3 border-0 text-center'><span className='text-uppercase fw-bold'>To Bag</span></th>
                                                                                <th scope='col' className='py-3 border-0 text-center'><span className='text-uppercase fw-bold'>Assigned Bags</span></th>
                                                                                <th scope='col' className='py-3 border-0 text-center'><span className='text-uppercase fw-bold'>Scanned</span></th>
                                                                                <th scope='col' className='py-3 border-0 text-center'><span className='text-uppercase fw-bold'>Unscanned</span></th>
                                                                                <th scope='col' className='py-3 border-0 text-center rounded-end pe-2'><span className='text-uppercase fw-bold'>Action</span></th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className='border-top-0'>
                                                                            {item.productDetails.map((item1: any, index: any) => (
                                                                                <tr key={item1.productAssignId || item1.dispatchProductDetailsId} className='transition-base'>
                                                                                    <td className='py-3 text-muted ps-3'>{index + 1}</td>
                                                                                    <td className='py-3 fw-semibold'>{item1.lotNo}</td>
                                                                                    <td className='py-3 fw-semibold'>
                                                                                        <div className='d-flex align-items-center gap-2'>
                                                                                            <span>{item1.countNo}</span>
                                                                                            {(item1.duplicateFlag === true || item1.duplicateFlag === 1 || item1.duplicateFlag === 'true') && (
                                                                                                <Icon icon='Warning' color='danger' size='lg' title='Duplicate Product' />
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className='py-3 text-center'>
                                                                                        <span>{item1.noOfCone ?? '-'}</span>
                                                                                    </td>
                                                                                    <td className='py-3 text-center'>
                                                                                        <span>{item1.coneWeight ?? '-'}</span>
                                                                                    </td>
                                                                                    <td className='py-3 text-center'>
                                                                                        <span>{item1.fromBagNo ?? '-'}</span>
                                                                                    </td>
                                                                                    <td className='py-3 text-center'>
                                                                                        <span>{item1.toBagNo ?? '-'}</span>
                                                                                    </td>
                                                                                    <td className='py-3 text-center'>
                                                                                        <span className='fw-bold text-primary'>{item1.assignedCount}</span>
                                                                                    </td>
                                                                                    <td className='py-3 text-center'>
                                                                                        <span className='fw-bold text-success'>
                                                                                            {Number(item1.scannedCount)}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className='py-3 text-center'>
                                                                                        <span className='fw-bold text-danger'>
                                                                                            {Number(item1.pendingCount)}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className='py-3 text-center pe-3'>
                                                                                        {(item1.assignedCount ?? item1.noOfProduct) == (item1.pendingCount ?? item1.totalScannedCount) ?
                                                                                            <Button
                                                                                                color='danger'
                                                                                                isLight
                                                                                                icon='Delete'
                                                                                                className='rounded-pill shadow-sm'
                                                                                                onClick={() => onDelete(item1.productAssignId || item1.dispatchProductDetailsId)}
                                                                                                title='Delete Item'
                                                                                            /> : '-'
                                                                                        }
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr> : null
                                                }
                                            </tbody>
                                        ))}
                                    </> : <NoDataMsg columnsCount={10} msg={noDataMsg || 'No data found'} />
                                    }
                                </table>
                            </div>
                        }
                    </CardBody>
                    <PaginationButtons className='d-print-none' data={items} label='items' setCurrentPage={setCurrentPage} currentPage={currentPage} perPage={perPage} setPerPage={setPerPage} />
                </Card>

                {/* ── Assign Modal ── */}
                <Modal
                    id='assignProductModal'
                    titleId='assignProductModalTitle'
                    isOpen={isOffCanvasOpen}
                    setIsOpen={setIsOffCanvasOpen}
                    isCentered
                    isScrollable
                    isStaticBackdrop
                    size='xl'
                >
                    <ModalHeader setIsOpen={setIsOffCanvasOpen}>
                        <ModalTitle id='assignProductModalTitle'>
                            {editingDispatchProductId ? 'Edit Assigned Product' : 'Assign Product'}
                        </ModalTitle>
                    </ModalHeader>

                    <ModalBody>
                        <div className='row g-3'>
                            <div className='col-5'>
                                <FormGroup id='companyDetailsId' label='Company Name' isFloating>
                                    <SearchableSelect
                                        isFloating
                                        ariaLabel='Company Name'
                                        placeholder='Select Company'
                                        onChange={(e: any) => setCompanyDetailsId(e)}
                                        value={companyDetailsId}
                                        onBlur={assignForm.handleBlur}
                                        isValid={assignForm.isValid}
                                        isTouched={assignForm.touched.companyDetailsId}
                                        invalidFeedback={assignForm.errors.companyDetailsId}
                                        list={companyList.map((c: any) => ({
                                            value: c.companyDetailsId,
                                            label: c.companyNameAndCityName ?? c.companyName,
                                        }))}
                                        required
                                        disabled={!!editingDispatchProductId}
                                        portalMenuToBody
                                        portalMenuZIndex={1060}
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-4'>
                                <FormGroup id='deliveryLocation' label='Delivery Location' isFloating>
                                    <Input
                                        id='deliveryLocation'
                                        type='text'
                                        placeholder='Delivery Location'
                                        value={assignForm.values.deliveryLocation}
                                        onChange={assignForm.handleChange}
                                        onBlur={assignForm.handleBlur}
                                        isValid={assignForm.isValid}
                                        isTouched={assignForm.touched.deliveryLocation}
                                        invalidFeedback={assignForm.errors.truckNo}
                                        disabled={!!editingDispatchProductId}
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-3'>
                                <FormGroup id='truckNo' label='Vehicle No' isFloating>
                                    <Input
                                        id='truckNo'
                                        type='text'
                                        placeholder='Vehicle No'
                                        value={assignForm.values.truckNo}
                                        onChange={assignForm.handleChange}
                                        onBlur={assignForm.handleBlur}
                                        isValid={assignForm.isValid}
                                        isTouched={assignForm.touched.truckNo}
                                        invalidFeedback={assignForm.errors.truckNo}
                                        disabled={!!editingDispatchProductId}
                                    />
                                </FormGroup>
                            </div>
                        </div>

                        {/* Dispatch rows — table layout */}
                        <div className='table-responsive mt-3'>
                            <table className='table table-modern align-top small' style={{ fontSize: '13px' }}>
                                <thead className='table-info'>
                                    <tr>
                                        <th style={{ width: '40px' }}>#</th>
                                        <th>Lot No <span className='text-danger'>*</span></th>
                                        <th>Count No <span className='text-danger'>*</span></th>
                                        <th style={{ width: '130px' }}>From Bag No</th>
                                        <th style={{ width: '130px' }}>To Bag No</th>
                                        <th style={{ width: '130px' }}>No of Bags <span className='text-danger'>*</span></th>
                                        <th style={{ width: '50px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dispatchRows.map((row: any, index: number) => (
                                        <tr key={index}>
                                            <td className='text-center fw-bold align-top' style={{ paddingTop: '19px' }}>{index + 1}</td>
                                            <td className='align-top'>
                                                <SearchableSelect
                                                    ariaLabel={`Lot No ${index}`}
                                                    placeholder='Select Lot'
                                                    onChange={(e: any) => onLotChange(index, e)}
                                                    value={row.lotId}
                                                    list={lotOptions}
                                                    portalMenuToBody
                                                    portalMenuZIndex={1060}
                                                    isValid={!row.lotError}
                                                    isTouched={!!row.lotError}
                                                    invalidFeedback={row.lotError}
                                                />
                                            </td>

                                            {/* Count */}
                                            <td className='align-top'>
                                                <SearchableSelect
                                                    ariaLabel={`Count No ${index}`}
                                                    placeholder='Select Count'
                                                    onChange={(e: any) => onCountChange(index, e)}
                                                    value={row.countId}
                                                    list={row.countOptions}
                                                    disabled={!row.lotId?.value}
                                                    portalMenuToBody
                                                    portalMenuZIndex={1060}
                                                    isValid={!row.countError && !row.duplicateError}
                                                    isTouched={!!row.countError || !!row.duplicateError}
                                                    invalidFeedback={row.countError || row.duplicateError}
                                                    formatOptionLabel={(option: any, { context }: any) => {
                                                        const isDuplicate = option?.allData?.duplicateFlag === true ||
                                                            option?.allData?.duplicateFlag === 1 ||
                                                            option?.allData?.duplicateFlag === 'true' ||
                                                            (option?.value === row?.allData?.productDetailsStockId && (row?.allData?.duplicateFlag === true || row?.allData?.duplicateFlag === 1 || row?.allData?.duplicateFlag === 'true'));
                                                        return (
                                                            <div className='d-flex align-items-center justify-content-between gap-2 w-100'>
                                                                <span>{option?.label}</span>
                                                                {isDuplicate && (
                                                                    <Icon
                                                                        icon='Warning'
                                                                        color='danger'
                                                                        size='lg'
                                                                        title='Duplicate Product'
                                                                        className={context === 'value' ? 'me-4' : ''}
                                                                    />
                                                                )}
                                                            </div>
                                                        );
                                                    }}
                                                />
                                            </td>

                                            {/* From Bag No */}
                                            <td className='align-top'>
                                                <Input
                                                    id={`fromBagNo-${index}`}
                                                    type='text'
                                                    placeholder='From Bag'
                                                    value={row.fromBagNo ?? ''}
                                                    onChange={(e: any) => onFromBagNoChange(index, e.target.value)}
                                                    disabled={!row.countId?.value}
                                                    isValid={!row.fromBagNoError}
                                                    isTouched={!!row.fromBagNoError}
                                                    invalidFeedback={row.fromBagNoError}
                                                />
                                            </td>

                                            {/* To Bag No */}
                                            <td className='align-top'>
                                                <Input
                                                    id={`toBagNo-${index}`}
                                                    type='text'
                                                    placeholder='To Bag'
                                                    value={row.toBagNo ?? ''}
                                                    onChange={(e: any) => onToBagNoChange(index, e.target.value)}
                                                    disabled={!row.countId?.value}
                                                    isValid={!row.toBagNoError}
                                                    isTouched={!!row.toBagNoError}
                                                    invalidFeedback={row.toBagNoError}
                                                />
                                            </td>

                                            {/* No of Bags */}
                                            <td className='align-top'>
                                                <Input
                                                    id={`noOfBags-${index}`}
                                                    type='number'
                                                    min={1}
                                                    max={row.maxProduct || undefined}
                                                    placeholder={row.maxProduct ? `Max: ${row.maxProduct}` : 'No of Bags'}
                                                    value={row.noOfProduct}
                                                    onChange={(e: any) => onNoOfProductChange(index, e.target.value)}
                                                    disabled={!row.countId?.value}
                                                    isValid={!row.noOfProductError}
                                                    isTouched={!!row.noOfProductError}
                                                    invalidFeedback={row.noOfProductError}
                                                />
                                            </td>

                                            {/* Action: Delete + Add on last row */}
                                            <td className='text-center align-top' style={{ whiteSpace: 'nowrap', paddingTop: '12px' }}>
                                                {dispatchRows.length > 1 && !(row.dispatchProductDetailsId > 0) && (
                                                    <Button
                                                        color='danger'
                                                        isLight
                                                        icon='Delete'
                                                        size='sm'
                                                        onClick={() => removeRow(index)}
                                                        className='me-1'
                                                    />
                                                )}
                                                {index === dispatchRows.length - 1 && (
                                                    <Button
                                                        color='info'
                                                        isLight
                                                        icon='Add'
                                                        size='sm'
                                                        onClick={addRow}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </ModalBody>
                    <ModalFooter>
                        <Button color='danger' isLink onClick={() => setIsOffCanvasOpen(false)}>
                            Close
                        </Button>
                        <Button color='primary' icon='Save' onClick={submitAssign}>
                            Submit
                        </Button>
                    </ModalFooter>
                </Modal>

                <AlertService setIsOpen={setIsAlertOpen} isOpen={isAlertOpen} alertStatus={alertStatus} />

                {/* ── Delete Confirmation Modal ── */}
                <Modal
                    id='deleteConfirmModal'
                    isOpen={isDeleteModalOpen}
                    setIsOpen={setIsDeleteModalOpen}
                    size='sm'
                >
                    <ModalHeader setIsOpen={setIsDeleteModalOpen}>
                        <></>
                    </ModalHeader>
                    <ModalBody>
                        <div className='text-center'>
                            <Icon icon='ReportProblem' size='3x' color='danger' className='mb-3' />
                            <h5 className='fw-bold'>Are you sure you want to delete?</h5>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color='info' isLight onClick={() => setIsDeleteModalOpen(false)} size={'sm'}>
                            Cancel
                        </Button>
                        <Button color='danger' onClick={confirmDelete} size={'sm'}>
                            Yes, Delete
                        </Button>
                    </ModalFooter>
                </Modal>

            </Page>
        </PageWrapper>
    )
}

export default AssignProducts